import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
    Translation,
    TranslocoLoader,
    TranslocoService,
} from '@jsverse/transloco';
import { lastValueFrom, retry, throwError, timer } from 'rxjs';
import { UserService } from '../services/user.service';
import { CognitoAuthService } from '../auth/cognito-auth.service';
import { HttpCacheManager, withCache } from '@ngneat/cashew';
import {
    buildTranslationCacheKey,
    cachedTranslationVersion,
    clearTranslationCache,
    currentTranslationVersion,
    rememberTranslationVersion,
    TRANSLATION_CACHE_STORAGE,
    TranslocoCacheTime,
} from './translation-cache';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private recoveringLangs = new Set<string>();
    private cacheVersionChecked = false;

    constructor(
        private httpClient: HttpClient,
        private userService: UserService,
        private cacheManager: HttpCacheManager,
        private cognitoAuthService: CognitoAuthService,
        private injector: Injector
    ) {}

    async getTranslation(lang: string): Promise<Translation> {
        let language: string;
        let module: string;
        if (lang.includes('/')) {
            const parts = lang.split('/');
            module = parts[0] ?? 'page';
            language = parts[1] ?? lang;
        } else {
            language = lang;
            module = 'page';
        }

        if (module === 'codext') {
            return await lastValueFrom(
                this.httpClient.get<Translation>(
                    `/assets/i18n/${language}.json`
                )
            );
        }

        // Translations edited in the backend must never be served from a stale
        // cache, so drop everything as soon as the backend's translation
        // timestamp moves on.
        this.ensureCacheVersion();

        const cacheKey = buildTranslationCacheKey(module, language);

        // System_translations.php answers 401 for module requests unless the
        // PHP session is logged in. Right after a Cognito login the token
        // exchange may still be in flight, so wait for it before fetching —
        // otherwise the empty result sticks in transloco's in-memory store
        // and users see literal keys until they reload the page.
        // A warm cache is answered from localStorage without a request, so
        // there is no 401 to avoid and no reason to block on the handshake.
        const warm = this.cacheManager.validate(
            cacheKey,
            TRANSLATION_CACHE_STORAGE
        );
        if (!warm) {
            await this.waitForSession();
        }

        // The cache entry is shared by every user, so an anonymous load (still
        // on the sign-in page, or a handshake that timed out) must not write to
        // it — its 401 would be handed to the next logged-in visitor. Reading a
        // warm entry stays fine.
        const useCache = warm || !!this.currentUid();

        const cached = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            useCache
        );
        if (this.isValidTranslation(cached)) {
            return cached;
        }

        // The cache delivered (or just stored) an empty/invalid payload — drop it
        // and try again live so users don't keep seeing literal translation keys
        // until the 1-day TTL expires.
        this.dropCacheEntry(cacheKey);
        const fresh = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            false
        );
        if (this.isValidTranslation(fresh)) {
            return fresh;
        }

        // Still nothing. Transloco will keep this empty result in memory for
        // the rest of the session, so retry in the background and push the
        // translations in once the backend answers.
        this.scheduleRecovery(lang, module, language, cacheKey);
        return {} as Translation;
    }

    private async waitForSession(): Promise<void> {
        // Safety net: never stall translation loading indefinitely.
        const timeout = new Promise<void>(resolve =>
            setTimeout(resolve, 10000)
        );
        await Promise.race([this.cognitoAuthService.ensureSession(), timeout]);
    }

    /**
     * Background self-healing for a failed module load: retry with backoff
     * and merge the translations into transloco's store so already-rendered
     * views recover without a full page reload.
     */
    private scheduleRecovery(
        lang: string,
        module: string,
        language: string,
        cacheKey: string
    ): void {
        if (this.recoveringLangs.has(lang)) {
            return;
        }
        this.recoveringLangs.add(lang);
        const sleep = (ms: number): Promise<void> =>
            new Promise(resolve => setTimeout(resolve, ms));
        const run = async (): Promise<void> => {
            // While anonymous the backend keeps answering 401 for module
            // requests, so don't burn the retries yet — wait until the user
            // signs in (e.g. they are still on the sign-in page).
            let waited = 0;
            const maxWait = 10 * 60 * 1000;
            while (!this.userService.getUser()?.uid && waited < maxWait) {
                await sleep(3000);
                waited += 3000;
            }
            for (const delay of [2000, 5000, 15000, 30000]) {
                await sleep(delay);
                const result = await this.fetchModuleTranslations(
                    module,
                    language,
                    cacheKey,
                    false
                );
                if (this.isValidTranslation(result)) {
                    // Lazy lookup — injecting TranslocoService in the
                    // constructor would be a circular dependency.
                    this.injector
                        .get(TranslocoService)
                        .setTranslation(result, lang);
                    return;
                }
            }
        };
        run().finally(() => this.recoveringLangs.delete(lang));
    }

    /**
     * Purges the whole translation cache once per app load when the backend
     * reports a newer translation revision than the cached responses were
     * fetched for. The revision itself is refreshed by the
     * `TranslationResolver` on every navigation.
     */
    private ensureCacheVersion(): void {
        if (this.cacheVersionChecked) {
            return;
        }
        this.cacheVersionChecked = true;

        const version = currentTranslationVersion();
        if (cachedTranslationVersion() === version) {
            return;
        }
        clearTranslationCache(this.cacheManager);
        rememberTranslationVersion(version);
    }

    private dropCacheEntry(cacheKey: string): void {
        this.cacheManager.delete(cacheKey, {
            strategy: TRANSLATION_CACHE_STORAGE,
        });
    }

    // Read through getUser() so a reload picks the uid back up from
    // sessionStorage — the pre-bootstrap loads run before anything else
    // populates the service.
    private currentUid(): string | undefined {
        return this.userService?.getUser()?.uid;
    }

    private async fetchModuleTranslations(
        module: string,
        language: string,
        cacheKey: string,
        useCache: boolean
    ): Promise<Translation | null> {
        const uid = this.currentUid();
        const baseUrl = `hippo/navigator.php?page=pages/system/System_translations.php&page_type=json${
            uid ? `&uid=${uid}` : ''
        }`;
        const formData = new FormData();
        formData.append('iso_code', language);
        formData.append('module', module);

        try {
            const options = useCache
                ? {
                      context: withCache({
                          ttl: TranslocoCacheTime,
                          key: cacheKey,
                          // Without this the response only lands in cashew's
                          // in-memory store and is gone after every reload.
                          storage: TRANSLATION_CACHE_STORAGE,
                      }),
                  }
                : {};
            const response = await lastValueFrom(
                this.httpClient
                    .post<{ result?: Translation }>(baseUrl, formData, options)
                    .pipe(
                        retry({
                            count: 2,
                            delay: (err: unknown, retryCount: number) => {
                                // Don't retry auth/permission errors — only transient ones.
                                if (err instanceof HttpErrorResponse) {
                                    const status = err.status;
                                    if (status >= 400 && status < 500) {
                                        return throwError(() => err);
                                    }
                                }
                                return timer(500 * retryCount);
                            },
                        })
                    )
            );
            const result = response?.result ?? null;
            if (useCache && !this.isValidTranslation(result)) {
                this.dropCacheEntry(cacheKey);
            }
            return result;
        } catch (error) {
            if (useCache) {
                this.dropCacheEntry(cacheKey);
            }
            return null;
        }
    }

    private isValidTranslation(value: unknown): value is Translation {
        return (
            !!value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value as object).length > 0
        );
    }
}
